/**
 * Declarative "this action needs these fields first" helper.
 *
 * A feature describes its prerequisites as a list of rules, then renders the resulting
 * gaps with `MissingRequiredFields` so the user gets a clickable "Requires ..." hint
 * instead of a silently disabled button.
 */

export interface MissingRequiredField {
  key: string;
  label: string;
  /** DOM id to scroll to when the label is clicked. Empty when the field has no anchor. */
  anchorId: string;
}

export interface RequiredFieldRule<TSource> {
  /** Stable identifier, also used as the React key when rendering. */
  key: string;
  /** User facing label, e.g. "registration number". */
  label: string;
  /** Resolves the DOM id the "Requires ..." link scrolls to. */
  getAnchorId?: (source: TSource) => string;
  /** Skips the rule when the field is not reachable, e.g. variant only exists in Glass's Guide mode. */
  isApplicable?: (source: TSource) => boolean;
  isSatisfied: (source: TSource) => boolean;
}

export const getMissingRequiredFields = <TSource>(
  rules: RequiredFieldRule<TSource>[],
  source: TSource,
): MissingRequiredField[] =>
  rules
    .filter((rule) => (rule.isApplicable ? rule.isApplicable(source) : true) && !rule.isSatisfied(source))
    .map(({ key, label, getAnchorId }) => ({ key, label, anchorId: getAnchorId?.(source) || "" }));

export const NO_MISSING_REQUIRED_FIELDS: MissingRequiredField[] = [];
